<template>
  <v-container fluid class="delivery-list-page pa-0">
    <v-card :loading="loading" outlined class="delivery-list-card">
      <div class="list-header">
        <div class="title-wrap">
          <v-btn icon small class="mr-2" @click="$router.go(-1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <div>
            <div class="page-title">Inbound Deliveries</div>
            <div class="page-subtitle">
              {{ deliveries.length }} delivery{{ deliveries.length === 1 ? "" : "ies" }} found
            </div>
          </div>
        </div>
      </div>

      <div class="filter-bar">
        <v-text-field v-model="search" outlined dense hide-details clearable
          prepend-inner-icon="search" label="Search reference or delivery person"
          class="delivery-search" />
        <v-spacer />
        <v-btn-toggle v-model="status" mandatory dense @change="getList">
          <v-btn small value="submitted">Waiting</v-btn>
          <v-btn small value="converted">Converted</v-btn>
          <v-btn small value="all">All</v-btn>
        </v-btn-toggle>
      </div>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box"><div class="summary-label">Waiting Review</div><div class="summary-value">{{ waitingCount }}</div></div>
          <div class="summary-box"><div class="summary-label">Total Boxes</div><div class="summary-value">{{ totalBoxes }}</div></div>
        </div>

        <v-data-table :headers="headers" :items="deliveries" :search="search"
          :loading="loading" :mobile-breakpoint="0" sort-by="createdAt"
          :sort-desc="true" class="delivery-table">
          <template v-slot:item="{ item }">
            <tr class="delivery-row" @click="view(item)">
              <td>
                <div class="font-weight-bold">{{ item.referenceNumber }}</div>
                <div class="cell-subtitle">{{ item.createdAt | moment("MM/DD/YYYY LT") }}</div>
              </td>
              <td>{{ item.DeliveryPerson ? item.DeliveryPerson.name : "Unknown" }}</td>
              <td class="text-center">{{ detailCount(item) }}</td>
              <td class="text-center font-weight-bold">{{ boxCount(item) }}</td>
              <td>
                <v-chip small :color="item.status === 'converted' ? 'green lighten-5' : 'orange lighten-5'"
                  :text-color="item.status === 'converted' ? 'green darken-3' : 'orange darken-3'">
                  {{ item.status === "converted" ? "Converted" : "Waiting" }}
                </v-chip>
              </td>
              <td class="text-right" @click.stop>
                <v-btn v-if="item.status !== 'converted'" small rounded color="primary" dark @click="convert(item)">Import</v-btn>
                <v-btn v-else-if="item.Purchase" small text color="primary" @click="openPurchase(item)">Purchase #{{ item.Purchase.localId }}</v-btn>
              </td>
            </tr>
          </template>
          <template v-slot:no-data>
            <div class="empty-state"><v-icon size="52" color="grey lighten-1">mdi-truck-check-outline</v-icon><div class="empty-title">No inbound deliveries</div><div class="cell-subtitle">Submitted warehouse deliveries will appear here.</div></div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import service from "../service";
import { mapActions, mapGetters } from "vuex";
import Hashids from "hashids";
const hashids = new Hashids();
export default {
  name: "InboundDeliveryList",
  data: () => ({
    loading: false, search: "", status: "submitted", deliveries: [],
    headers: [
      { text: "Delivery", value: "referenceNumber" },
      { text: "Received By", value: "DeliveryPerson.name" },
      { text: "Entries", value: "entries", align: "center", sortable: false },
      { text: "Boxes", value: "boxes", align: "center", sortable: false },
      { text: "Status", value: "status" },
      { text: "", value: "action", sortable: false },
    ],
  }),
  computed: {
    ...mapGetters("purchase", ["PurchaseDetails"]),
    waitingCount() { return this.deliveries.filter((row) => row.status !== "converted").length; },
    totalBoxes() { return this.deliveries.reduce((sum, row) => sum + this.boxCount(row), 0); },
  },
  created() { this.getList(); document.title = "smplsale - Inbound Deliveries"; },
  methods: {
    ...mapActions("purchase", ["ResetPurchase"]),
    detailCount(item) { return item.Details ? item.Details.length : 0; },
    boxCount(item) { return (item.Details || []).reduce((sum, row) => sum + Number(row.quantity || 0), 0); },
    async getList() {
      this.loading = true;
      try { const response = await service.getAll({ status: this.status }); this.deliveries = response.data || []; }
      catch (error) { this.deliveries = []; this.$swal("Inbound Deliveries", error.data && error.data.message ? error.data.message : "Unable to load deliveries.", "error"); }
      finally { this.loading = false; }
    },
    view(item) { this.$router.push(`/inventory/inboundDelivery/${item.id}/view`); },
    async convert(item) {
      if (this.PurchaseDetails.length) {
        const result = await this.$swal({ title: "Replace running purchase?", text: "Importing this delivery will replace the current unsaved purchase.", icon: "warning", showCancelButton: true, confirmButtonText: "Continue" });
        if (!result.value) return;
      }
      this.ResetPurchase();
      this.$router.push(`/inventory/purchase/create?InboundDeliveryId=${item.id}`);
    },
    openPurchase(item) {
      const hashId = hashids.encode(item.Purchase.id);
      const routeData = this.$router.resolve({
        path: `/global/purchase/${hashId}/view`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
  },
};
</script>

<style scoped>
.delivery-list-page{background:#f8fafc}.delivery-list-card{border-radius:20px;background:#fff}.list-header{padding:16px;border-bottom:1px solid #e2e8f0;display:flex;justify-content:space-between}.title-wrap{display:flex;align-items:center}.page-title{font-size:22px;font-weight:800;color:#0f172a}.page-subtitle,.cell-subtitle{font-size:12px;color:#64748b;margin-top:2px}.filter-bar{padding:12px 16px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;gap:12px;flex-wrap:wrap}.delivery-search{max-width:360px;min-width:260px}.summary-row{display:grid;grid-template-columns:repeat(2,minmax(0,220px));gap:12px}.summary-box{background:#f8fafc;border-radius:16px;padding:14px}.summary-label{font-size:12px;color:#64748b}.summary-value{font-size:22px;font-weight:800;color:#0f172a;margin-top:4px}.delivery-table{border:1px solid #e2e8f0;border-radius:16px;overflow:hidden}.delivery-row{cursor:pointer}.delivery-row:hover{background:#f8fafc}.empty-state{text-align:center;padding:42px 16px}.empty-title{font-size:17px;font-weight:800;color:#0f172a;margin-top:8px}@media(max-width:600px){.delivery-search{max-width:100%;min-width:100%}.summary-row{grid-template-columns:1fr}}
</style>
